/**
 * "Copy from another customer" — copy another customer's special job-type prices onto this one
 * (gap 1 PR 2, DEC-383, D-11).
 *
 * Pick the source customer and a start date (today or later), preview, untick any row you don't
 * want, save. Only job types this customer may use are offered (the API plans the rows). A day
 * that already has a change says so in the preview (D-08).
 *
 * T-gap1-38b: unticking re-previews with `familyIds` = the ticked job types, so the token the
 * save sends covers exactly the rows that will be written. An unticked row stays in the table
 * as "No change" so it can be ticked again. The API is the gate (MANAGE_CUSTOMER_PRICES).
 */

import React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import type { CustomerContext } from '../../routes/CustomerPricesPage';
import { PriceChangePreview, type PreviewColumn } from './PriceChangePreview';
import { StartDateInput } from './StartDateInput';
import { usePreviewApply } from './use-preview-apply';
import {
  formatDay,
  priceText,
  type JobTypeBulkApplyResponse,
  type JobTypeBulkRequest,
  type JobTypePreviewRow,
} from './customer-prices';
import { BULK_APPLY_PATH, BULK_PREVIEW_PATH, CUSTOMER_PRICES_KEY } from './customer-prices-hooks';
import { useFeeCustomers } from './customer-disposal-fees-hooks';

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  marginBottom: '4px',
};
const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };
const errorStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-danger)' };
const selectStyle: React.CSSProperties = {
  minHeight: '38px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

type Row = JobTypePreviewRow;

const NOT_COPIED = 'Not copied.';

function cents(row: Row, value: number | null): string {
  if (value === null) return 'No price';
  return priceText(value, row.jobType?.billingMethod ?? '');
}

const COLUMNS: readonly PreviewColumn<Row>[] = [
  { key: 'jobType', header: 'Job type', cell: (r) => r.jobType?.label ?? '—' },
  { key: 'now', header: 'Now', cell: (r) => cents(r, r.currentCents) },
  { key: 'new', header: 'New', cell: (r) => (r.newCents === null ? '—' : cents(r, r.newCents)) },
  { key: 'from', header: 'From', cell: (r) => formatDay(r.effectiveFrom) },
];

export interface CopyJobTypePricesDialogProps {
  readonly customer: CustomerContext;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function CopyJobTypePricesDialog(props: CopyJobTypePricesDialogProps): React.JSX.Element {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent style={{ maxWidth: '760px' }}>
        {/* Mounted per opening (Radix unmounts closed content), so every opening starts fresh. */}
        <CopyForm
          customer={props.customer}
          onDone={(): void => {
            props.onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function CopyForm(p: { customer: CustomerContext; onDone: () => void }): React.JSX.Element {
  const { customer, onDone } = p;
  const customers = useFeeCustomers();
  const bulk = usePreviewApply<JobTypeBulkRequest, Row, JobTypeBulkApplyResponse>({
    previewPath: BULK_PREVIEW_PATH,
    applyPath: BULK_APPLY_PATH,
    invalidate: [[...CUSTOMER_PRICES_KEY, customer.id]],
  });

  const [fromId, setFromId] = React.useState('');
  const [effectiveFrom, setEffectiveFrom] = React.useState(customer.today);
  const [problem, setProblem] = React.useState<string | null>(null);
  /** Every row the full preview offered, kept so an unticked row can be ticked again. */
  const [offered, setOffered] = React.useState<readonly Row[] | null>(null);
  const [ticked, setTicked] = React.useState<ReadonlySet<string>>(new Set());

  const sources = (customers.data?.customers ?? []).filter((c) => c.id !== customer.id);

  // The full preview (no familyIds) fills `offered` and ticks every row.
  const waitingForFull = React.useRef(false);
  React.useEffect(() => {
    if (!waitingForFull.current || bulk.status !== 'ready' || bulk.rows === null) return;
    waitingForFull.current = false;
    setOffered(bulk.rows);
    setTicked(new Set(bulk.rows.flatMap((r) => (r.jobType === null ? [] : [r.jobType.familyId]))));
  }, [bulk.status, bulk.rows]);

  React.useEffect(() => {
    if (bulk.status !== 'done' || bulk.result === null) return;
    const n = bulk.result.applied.add + bulk.result.applied.change;
    toast.success(`Copied ${String(n)} ${n === 1 ? 'price' : 'prices'} to ${customer.name}.`);
    onDone();
  }, [bulk.status, bulk.result, customer.name, onDone]);

  function request(familyIds: readonly string[] | null): JobTypeBulkRequest {
    return {
      kind: 'COPY',
      fromCustomerId: fromId,
      toCustomerId: customer.id,
      ...(familyIds !== null && { familyIds }),
      effectiveFrom,
    };
  }

  function previewAll(): void {
    if (fromId === '') {
      setProblem('Pick the customer to copy from.');
      return;
    }
    if (effectiveFrom === '' || effectiveFrom < customer.today) {
      setProblem('Start dates can be today or later.');
      return;
    }
    setProblem(null);
    setOffered(null);
    waitingForFull.current = true;
    void bulk.preview(request(null));
  }

  /** Re-preview exactly the ticked job types, so the saved token covers only them. */
  function previewTicked(next: ReadonlySet<string>): void {
    if (offered === null) return;
    if (next.size === 0) {
      bulk.reset();
      return;
    }
    const ids = offered.flatMap((r) =>
      r.jobType !== null && next.has(r.jobType.familyId) ? [r.jobType.familyId] : [],
    );
    void bulk.preview(request(ids));
  }

  function changeInputs(fn: () => void): void {
    fn();
    setProblem(null);
    setOffered(null);
    setTicked(new Set());
    waitingForFull.current = false;
    bulk.reset();
  }

  // Ticked rows read from the latest preview (fresh status); unticked ones say "Not copied."
  const latest = new Map(
    (bulk.rows ?? []).flatMap((r) => (r.jobType === null ? [] : [[r.jobType.familyId, r]])),
  );
  const shown: Row[] | null =
    offered === null
      ? null
      : offered.map((r) => {
          if (r.jobType === null) return r;
          if (!ticked.has(r.jobType.familyId)) {
            const skipped: Row = {
              ...r,
              status: 'UNCHANGED',
              newCents: null,
              messages: [NOT_COPIED],
            };
            return skipped;
          }
          return latest.get(r.jobType.familyId) ?? r;
        });
  const toSave =
    bulk.rows === null
      ? 0
      : bulk.rows.filter(
          (r) =>
            (r.status === 'ADD' || r.status === 'CHANGE') &&
            r.jobType !== null &&
            ticked.has(r.jobType.familyId),
        ).length;
  const busy = bulk.status === 'previewing' || bulk.status === 'applying';

  return (
    <>
      <DialogHeader>
        <DialogTitle>{`Copy prices to ${customer.name}`}</DialogTitle>
        <DialogDescription>
          Copies another customer&apos;s special prices, for the job types {customer.name} can use.
          Applies to tickets billed on or after the start date.
        </DialogDescription>
      </DialogHeader>
      <div style={{ display: 'grid', gap: '12px' }}>
        <div>
          <label htmlFor="copy-prices-from" style={labelStyle}>
            Copy from
          </label>
          <select
            id="copy-prices-from"
            value={fromId}
            style={selectStyle}
            onChange={(e): void => changeInputs(() => setFromId(e.target.value))}
          >
            <option value="">Choose a customer…</option>
            {sources.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name === null ? c.abbreviation : `${c.abbreviation} — ${c.name}`}
              </option>
            ))}
          </select>
          {customers.isError && (
            <p role="alert" style={errorStyle}>
              Could not load the customers. Try again.
            </p>
          )}
        </div>
        <StartDateInput
          value={effectiveFrom}
          today={customer.today}
          onChange={(v): void => changeInputs(() => setEffectiveFrom(v))}
        />
        {problem !== null && (
          <p role="alert" style={errorStyle}>
            {problem}
          </p>
        )}
        <div>
          <Button type="button" variant="outline" disabled={busy} onClick={previewAll}>
            Preview
          </Button>
        </div>
        {bulk.status === 'previewing' && (
          <p role="status" style={helpStyle}>
            Working out the prices…
          </p>
        )}
        {bulk.status === 'error' && bulk.error !== null && (
          <p role="alert" style={errorStyle}>
            {bulk.error}
          </p>
        )}
        {bulk.status === 'stale' && (
          <div role="alert" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={errorStyle}>{bulk.error}</span>
            <Button type="button" size="sm" variant="outline" onClick={previewAll}>
              Preview again
            </Button>
          </div>
        )}
        {shown !== null &&
          (shown.length === 0 ? (
            <p style={helpStyle}>That customer has no special prices this customer can use.</p>
          ) : (
            <PriceChangePreview
              rows={shown}
              columns={COLUMNS}
              selectable={{
                isSelected: (r): boolean => r.jobType !== null && ticked.has(r.jobType.familyId),
                toggle: (r): void => {
                  if (r.jobType === null) return;
                  const next = new Set(ticked);
                  if (next.has(r.jobType.familyId)) next.delete(r.jobType.familyId);
                  else next.add(r.jobType.familyId);
                  setTicked(next);
                  previewTicked(next);
                },
              }}
            />
          ))}
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!bulk.canApply || toSave === 0}
          onClick={(): void => {
            void bulk.apply();
          }}
        >
          Save
        </Button>
      </DialogFooter>
    </>
  );
}
